package gr.tech.meetinggenie.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronDown
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronRight
import com.varabyte.kobweb.silk.components.icons.fa.FaEllipsisVertical
import com.varabyte.kobweb.silk.components.icons.fa.FaPlus
import com.varabyte.kobweb.silk.components.icons.fa.IconSize
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import gr.tech.meetinggenie.components.widgets.loadingSpinner
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.meetinggenie.components.layouts.AuthPageLayout
import gr.tech.meetinggenie.components.widgets.ConfirmModal
import gr.tech.meetinggenie.components.widgets.CreateMeetingModal
import gr.tech.meetinggenie.infrastructure.api.archiveMeeting
import gr.tech.meetinggenie.infrastructure.api.createMeeting
import gr.tech.meetinggenie.infrastructure.api.deleteMeeting
import gr.tech.meetinggenie.infrastructure.api.fetchMeetings
import gr.tech.meetinggenie.models.meeting.MEETING_STATUS
import gr.tech.meetinggenie.models.meeting.MeetingDTO
import gr.tech.meetinggenie.models.user.USER_ROLES
import gr.tech.meetinggenie.navigation.SitePage
import gr.tech.meetinggenie.sections.MeetingsListTopbar
import gr.tech.meetinggenie.styles.*
import gr.tech.meetinggenie.util.CONSTANTS
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Page(routeOverride = "meetings")
@Composable
fun MeetingListPage() {
    AuthPageLayout(
        title = "Weekly Meetings",
        requiredRoles = listOf(USER_ROLES.CONFIRMED_USER.role)
    ) {
        MeetingListScreen()
    }
}

@Composable
fun MeetingListScreen() {
    val ctx   = rememberPageContext()
    val scope = rememberCoroutineScope()

    val meetingsList       = remember { mutableStateListOf<MeetingDTO>() }
    var showLoading        by remember { mutableStateOf(true) }
    var showCreateModal    by remember { mutableStateOf(false) }

    // Pagination
    var currentPage        by remember { mutableStateOf(1) }
    var hasNextPage        by remember { mutableStateOf(false) }

    // Status filter: ACTIVE = no filter (OPEN + CLOSED); otherwise exact status match
    var statusFilter       by remember { mutableStateOf(MEETING_STATUS.OPEN) }
    var showFilterDropdown by remember { mutableStateOf(false) }

    // Per-row actions dropdown
    var openDropdownId     by remember { mutableStateOf<Int?>(null) }

    // Confirm modal state
    var confirmAction      by remember { mutableStateOf<String?>(null) } // "archive" or "delete"
    var confirmMeetingId   by remember { mutableStateOf<Int?>(null) }

    // Create Modal State
    var createLoading by remember { mutableStateOf(false) }
    var createError by remember { mutableStateOf("") }

    // Re-fetch whenever page or status filter changes
    LaunchedEffect(currentPage, statusFilter) {
        showLoading = true
        meetingsList.clear()
        val skip = (currentPage - 1) * CONSTANTS.ROWS_PER_FETCH_REQ
        fetchMeetings(
            skip   = skip,
            status = statusFilter,
            onSuccess = { list ->
                meetingsList.addAll(list)
                hasNextPage = list.size >= CONSTANTS.ROWS_PER_FETCH_REQ
                showLoading = false
            },
            onError = { showLoading = false }
        )
    }

    // Confirm modal
    if (confirmAction != null && confirmMeetingId != null) {
        val action    = confirmAction!!
        val meetingId = confirmMeetingId!!
        ConfirmModal(
            actionText = action,
            subText =
                if (action == "delete")
                    "This will soft-delete the meeting and record who deleted it."
                else
                    "This will move the meeting to the archived view.",
            onConfirm = {
                scope.launch {
                    if (action == "archive") {
                        archiveMeeting(meetingId,
                            onSuccess = { meetingsList.removeAll { it.id == meetingId } },
                            onError   = {}
                        )
                    } else {
                        deleteMeeting(meetingId,
                            onSuccess = { meetingsList.removeAll { it.id == meetingId } },
                            onError   = {}
                        )
                    }
                    confirmAction    = null
                    confirmMeetingId = null
                }
            },
            onDismiss = {
                confirmAction    = null
                confirmMeetingId = null
            }
        )
    }

    CreateMeetingModal(
        isOpen    = showCreateModal,
        isLoading = createLoading,
        error = createError,
        onDismiss = { showCreateModal = false },
        onCreate = { title ->
            scope.launch {
                createLoading = true
                createMeeting(
                    title     =  title.trim(),
                    onSuccess = { meeting ->
                        ctx.router.tryRoutingTo("${SitePage.Meeting.route}/${meeting.id}")
                        createLoading = false;
                    },
                    onError   = { msg ->
                        createLoading = false;
                        createError = msg
                    }
                )
            }

        }
    )

    // Global backdrop to close any open dropdown
    if (openDropdownId != null || showFilterDropdown) {
        Div(
            attrs = Modifier
                .position(Position.Fixed)
                .top(0.px).left(0.px)
                .width(100.percent).height(100.percent)
                .zIndex(50)
                .onClick {
                    openDropdownId     = null
                    showFilterDropdown = false
                }
                .toAttrs()
        )
    }

    Column(modifier = Modifier.fillMaxWidth()) {
        MeetingsListTopbar()

        Box(
            modifier = Modifier.fillMaxSize().padding(topBottom = 24.px, leftRight = 16.px),
            contentAlignment = Alignment.TopCenter
        ) {
            Column(
                modifier = ContentCardStyle.toModifier().fillMaxWidth().maxWidth(1000.px)
            ) {
                // Header row
                SimpleGrid(
                    numColumns = numColumns(base = 1, md = 2),
                    modifier = Modifier.fillMaxWidth().margin(bottom = 12.px),
                ) {
                    H1(ListPageTitleStyle.toModifier().toAttrs()) { Text("Weekly Meetings") }

                    Row(
                        modifier = Modifier.fillMaxWidth().gap(8.px),
                        horizontalArrangement = Arrangement.End,
                        verticalAlignment = Alignment.CenterVertically
                    ) {

                        // Status filter dropdown
                        Box(modifier = Modifier.position(Position.Relative).zIndex(51)) {
                            Button(
                               attrs = ButtonStyle.toModifier().color(Colors.Black).backgroundColor(Colors.WhiteSmoke)
                                   .toAttrs {
                                        onClick {
                                            showFilterDropdown = !showFilterDropdown
                                            openDropdownId = null
                                        }
                                 },
                            ) {
                                Text(statusFilter.label)
                                FaChevronDown(modifier = Modifier.margin(left = 6.px), size = IconSize.SM)
                            }

                            if (showFilterDropdown) {
                                Column(modifier = DropdownColumnStyle.toModifier()) {
                                    listOf(
                                        MEETING_STATUS.OPEN,
                                        MEETING_STATUS.ARCHIVED,
                                        MEETING_STATUS.DELETED,
                                    ).forEach { value ->
                                        Div(attrs = DropdownOptionStyle.toModifier()
                                                .backgroundColor(if (statusFilter == value) Colors.LightGray else Colors.White)
                                                .onClick {
                                                    if (statusFilter != value) {
                                                        statusFilter = value
                                                        currentPage  = 1
                                                    }
                                                    showFilterDropdown = false
                                                }
                                                .toAttrs()
                                        ) { Text(value.label) }
                                    }
                                }
                            }
                        }

                        // New Meeting button
                        Button(
                            attrs = ButtonStyle.toModifier().color(Colors.White)
                                .toAttrs {
                                    onClick {
                                       showCreateModal = true
                                    }
                                }
                        ) {
                            FaPlus(modifier = Modifier.margin(right = 6.px), size = IconSize.SM)
                            Text("Meeting")
                        }
                    }
                }

                // Table
                Column(modifier = TableContainerStyle.toModifier()) {
                    Table(attrs = BaseTableStyle.toAttrs()) {
                        Thead {
                            Tr(Modifier.fillMaxWidth().toAttrs()) {
                                Th(TableHeaderStyle.toModifier().then(LargeColumnStyle).toAttrs())  { Text("Meeting") }
                                Th(TableHeaderStyle.toModifier().then(MediumColumnStyle).toAttrs()) { Text("Responses") }
                                Th(TableHeaderStyle.toModifier().then(MediumColumnStyle).toAttrs()) { Text("Status") }
                                Th(TableHeaderStyle.toModifier().then(MediumColumnStyle).toAttrs()) { Text("Created By") }
                                Th(TableHeaderStyle.toModifier().width(40.px).toAttrs()) {}
                            }
                        }
                        Tbody {
                            if (showLoading) {
                                Tr {
                                    Td(Modifier.toAttrs { attr("colspan", "5") }) {
                                        loadingSpinner(iconSize = IconSize.LG)
                                    }
                                }
                                repeat(9) {
                                    Tr { Td(Modifier.height(48.px).toAttrs { attr("colspan", "5") }) {} }
                                }
                            } else if (meetingsList.isEmpty()) {
                                Tr {
                                    Td(Modifier.toAttrs { attr("colspan", "5") }) {
                                        SpanText(
                                            "No ${statusFilter.label.lowercase()} meetings found.",
                                            modifier = TableCellTextStyle.toModifier()
                                        )
                                    }
                                }
                                repeat(9) {
                                    Tr { Td(Modifier.height(48.px).toAttrs { attr("colspan", "5") }) {} }
                                }
                            } else {
                                meetingsList.forEach { meeting ->
                                    Tr(
                                        attrs = TableRowHoverStyle.toModifier()
                                            .onClick { ctx.router.tryRoutingTo("${SitePage.Meeting.route}/${meeting.id}") }
                                            .toAttrs()
                                    ) {
                                        Td(LargeColumnStyle.toAttrs()) {
                                            SpanText(meeting.title, modifier = TableCellBoldTextStyle.toModifier())
                                        }
                                        Td(SmallColumnStyle.toAttrs()) {
                                            SpanText(meeting.responseCount.toString(), modifier = TableCellTextStyle.toModifier())
                                        }
                                        Td(SmallColumnStyle.toAttrs()) {
                                            val status = MEETING_STATUS.parseStatus(meeting.status)
                                            val color = when (status) {
                                                MEETING_STATUS.OPEN     -> Colors.Green
                                                MEETING_STATUS.ARCHIVED -> Colors.DarkOrange
                                                MEETING_STATUS.DELETED  -> Colors.Red
                                                else                    -> Colors.Gray
                                            }
                                            SpanText(status.label, modifier = TableCellBoldTextStyle.toModifier().color(color))
                                        }
                                        Td(MediumColumnStyle.toAttrs()) {
                                            SpanText(meeting.createdBy.toString(), modifier = TableCellTextStyle.toModifier())
                                        }
                                        // 3-dot actions column
                                        Td(
                                            attrs = Modifier
                                                .width(40.px)
                                                .position(Position.Relative)
                                                .toAttrs()
                                        ) {
                                            Div(
                                                attrs = Modifier
                                                    .onClick { it.stopPropagation() }
                                                    .position(Position.Relative)
                                                    .zIndex(51)
                                                    .toAttrs()
                                            ) {
                                                // 3-dots button
                                                Div(
                                                    attrs = Modifier
                                                        .padding(leftRight = 8.px, topBottom = 6.px)
                                                        .borderRadius(4.px)
                                                        .cursor(Cursor.Pointer)
                                                        .onClick {
                                                            it.stopPropagation()
                                                            openDropdownId     = if (openDropdownId == meeting.id) null else meeting.id
                                                            showFilterDropdown = false
                                                        }
                                                        .toAttrs()
                                                ) {
                                                    FaEllipsisVertical(size = IconSize.SM)
                                                }

                                                // Dropdown menu
                                                if (openDropdownId == meeting.id) {
                                                    Column(
                                                        modifier = Modifier
                                                            .position(Position.Absolute)
                                                            .top(100.percent).right(0.px)
                                                            .backgroundColor(Colors.White)
                                                            .border(1.px, LineStyle.Solid, Colors.LightGray)
                                                            .borderRadius(6.px)
                                                            .padding(4.px)
                                                            .minWidth(120.px)
                                                            .zIndex(52)
                                                            .boxShadow(offsetY = 4.px, blurRadius = 8.px, color = rgba(0, 0, 0, 0.12))
                                                    ) {
                                                        Div(
                                                            attrs = Modifier
                                                                .fillMaxWidth()
                                                                .padding(leftRight = 12.px, topBottom = 8.px)
                                                                .borderRadius(4.px)
                                                                .cursor(Cursor.Pointer)
                                                                .fontFamily(CONSTANTS.FONT_FAMILY2)
                                                                .fontSize(0.875.cssRem)
                                                                .onClick {
                                                                    it.stopPropagation()
                                                                    openDropdownId   = null
                                                                    confirmAction    = "archive"
                                                                    confirmMeetingId = meeting.id
                                                                }
                                                                .toAttrs()
                                                        ) { Text("Archive") }

                                                        Div(
                                                            attrs = Modifier
                                                                .fillMaxWidth()
                                                                .padding(leftRight = 12.px, topBottom = 8.px)
                                                                .borderRadius(4.px)
                                                                .cursor(Cursor.Pointer)
                                                                .fontFamily(CONSTANTS.FONT_FAMILY2)
                                                                .fontSize(0.875.cssRem)
                                                                .color(Colors.Red)
                                                                .onClick {
                                                                    it.stopPropagation()
                                                                    openDropdownId   = null
                                                                    confirmAction    = "delete"
                                                                    confirmMeetingId = meeting.id
                                                                }
                                                                .toAttrs()
                                                        ) { Text("Delete") }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }

                                // Pad with empty rows up to a minimum of 10
                                val emptyRows = maxOf(0, 10 - meetingsList.size)
                                repeat(emptyRows) {
                                    Tr { Td(Modifier.height(48.px).toAttrs { attr("colspan", "5") }) {} }
                                }
                            }
                        }
                    }

                    // Pagination bar
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(topBottom = 10.px, leftRight = 12.px),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Button(
                            size = ButtonSize.SM,
                            enabled = currentPage > 1 && !showLoading,
                            onClick = { currentPage-- }
                        ) {
                            FaChevronLeft(modifier = Modifier.margin(right = 4.px), size = IconSize.SM)
                            Text("Prev")
                        }

                        SpanText(
                            "Page $currentPage",
                            modifier = TableCellTextStyle.toModifier()
                                .fontFamily(CONSTANTS.FONT_FAMILY2)
                                .fontSize(0.875.cssRem)
                        )

                        Button(
                            size = ButtonSize.SM,
                            enabled = hasNextPage && !showLoading,
                            onClick = { currentPage++ }
                        ) {
                            Text("Next")
                            FaChevronRight(modifier = Modifier.margin(left = 4.px), size = IconSize.SM)
                        }
                    }
                }
            }
        }
    }
}
